import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Paper,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Info as InfoIcon, TextFields as TextFieldsIcon } from '@mui/icons-material';
import ImageGrid from '../components/common/ImageGrid';
import { useImages } from '../context/images/ImagesContext';
import { useServices } from '../context/services/ServicesContext';
import api from '../services/api/apiService';
import ProcessingModal from '../components/common/ProcessingModal';
import { toast } from 'react-toastify';


const CroppedCaptionedPage = () => {
  const { croppedImages, loading, error, selectedInfluencer, deleteImage, fetchImagesByFolder } = useImages();
  const { processingService } = useServices();
  const [page, setPage] = useState(1);
  const [imagesPerPage, setImagesPerPage] = useState(48);
  const [filteredImages, setFilteredImages] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const [captionDialogOpen, setCaptionDialogOpen] = useState(false);
  const [caption, setCaption] = useState('');
  const [savingCaption, setSavingCaption] = useState(false);
  const [captionError, setCaptionError] = useState(null);
  const [showingCaption, setShowingCaption] = useState(false);
  const [zipSnackbarOpen, setZipSnackbarOpen] = useState(false);
  const [zipSnackbarMessage, setZipSnackbarMessage] = useState('');
  const [zipSnackbarSeverity, setZipSnackbarSeverity] = useState('success');
  
  // New states for image metadata dialog
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metrics, setMetrics] = useState('');
  const [score, setScore] = useState('');
  const [textFileContent, setTextFileContent] = useState('');
  const [loadingTextFile, setLoadingTextFile] = useState(false);
  const [savingMetadata, setSavingMetadata] = useState(false);
  const [metadataError, setMetadataError] = useState(null);
  
  // Captioning state
  const [selectedForCaption, setSelectedForCaption] = useState(new Set());
  const [captionMode, setCaptionMode] = useState(false);
  
  // Processing modal state
  const [processingModal, setProcessingModal] = useState({
    open: false,
    title: '',
    status: '',
    progress: null,
    error: null
  });

  // Fetch cropped images when component mounts or selectedInfluencer changes
  useEffect(() => {
    if (selectedInfluencer && selectedInfluencer.id) {
      // Only fetch if we don't have data or if the influencer changed
      fetchImagesByFolder('cropped', selectedInfluencer.id);
    }
  }, [selectedInfluencer?.id, fetchImagesByFolder]);

  // Filter and paginate images
  useEffect(() => {
    if (croppedImages.length > 0) {
      // Filter by selected influencer since API might return wrong influencer's images
      const filtered = selectedInfluencer
        ? croppedImages.filter(img => {
            const imgInfluencerId = img.influencerId || img.influencer_id;
            return imgInfluencerId === selectedInfluencer.id;
          })
        : croppedImages;
      
      setFilteredImages(filtered);
      setTotalPages(Math.ceil(filtered.length / imagesPerPage));
      
      // Reset to page 1 if current page is out of bounds
      if (page > Math.ceil(filtered.length / imagesPerPage)) {
        setPage(1);
      }
    } else {
      setFilteredImages([]);
      setTotalPages(1);
      setPage(1);
    }
  }, [croppedImages, selectedInfluencer, imagesPerPage, page]);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImagesPerPageChange = (event) => {
    setImagesPerPage(event.target.value);
    setPage(1); // Reset to first page when changing items per page
  };

  const handleOpenCaptionDialog = (image) => {
    setSelectedImage(image);
    setCaption(image.caption || '');
    setCaptionError(null);
    setCaptionDialogOpen(true);
  };

  const handleCloseCaptionDialog = () => {
    setCaptionDialogOpen(false);
    setSelectedImage(null);
    setCaption('');
  };

  const handleCaptionChange = (e) => {
    setCaption(e.target.value);
  };

  const handleSaveCaption = async () => {
    if (!selectedImage) return;
    
    setSavingCaption(true);
    setCaptionError(null);
    
    try {
      await api.put(`/images/${selectedImage.id}/caption`, { caption });
      
      // Update the image in the local state
      const updatedImages = croppedImages.map(img => 
        img.id === selectedImage.id ? { ...img, caption } : img
      );
      
      // Close the dialog
      handleCloseCaptionDialog();
    } catch (error) {
      console.error('Error saving caption:', error);
      setCaptionError('Failed to save caption');
    } finally {
      setSavingCaption(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      const success = await deleteImage(imageId, 'cropped');
      if (success) {
        setZipSnackbarMessage('Image deleted successfully');
        setZipSnackbarSeverity('success');
        setZipSnackbarOpen(true);
      } else {
        setZipSnackbarMessage('Failed to delete image. Please try again.');
        setZipSnackbarSeverity('error');
        setZipSnackbarOpen(true);
      }
    }
  };

  const handleEditCaption = (imageId) => {
    const image = filteredImages.find(img => img.id === imageId);
    if (image) {
      handleOpenCaptionDialog(image);
    }
  };

  const handleCloseSnackbar = () => {
    setZipSnackbarOpen(false);
  };
  
  // Get current images for pagination
  const getCurrentImages = () => {
    const startIndex = (page - 1) * imagesPerPage;
    const endIndex = startIndex + imagesPerPage;
    return filteredImages.slice(startIndex, endIndex);
  };
  
  // Check for images without captions
  const getImagesWithoutCaptions = () => {
    return filteredImages.filter(img => !img.caption || img.caption.trim() === '');
  };
  
  // Handle caption all images without captions
  const handleCaptionAll = async () => {
    const imagesWithoutCaptions = getImagesWithoutCaptions();
    
    if (imagesWithoutCaptions.length === 0) {
      toast.info('All images already have captions');
      return;
    }
    
    await captionImages(imagesWithoutCaptions);
  };
  
  // Handle caption selected images
  const handleCaptionSelected = async () => {
    if (selectedForCaption.size === 0) {
      toast.warning('Please select images to caption');
      return;
    }
    
    const imagesToCaption = filteredImages.filter(img => selectedForCaption.has(img.id));
    await captionImages(imagesToCaption);
  };
  
  // Common caption processing function
  const captionImages = async (images) => {
    setProcessingModal({
      open: true,
      title: `Generating captions for ${images.length} images...`,
      status: 'submitting',
      progress: { total: images.length, completed: 0 },
      error: null
    });
    
    try {
      const response = await processingService.captionImages(
        selectedInfluencer.id,
        images,
        (progress) => {
          setProcessingModal(prev => ({
            ...prev,
            status: 'processing',
            progress: progress
          }));
        }
      );
      
      if (response.jobId) {
        setProcessingModal(prev => ({
          ...prev,
          status: 'processing',
          progress: { ...prev.progress, message: 'Generating captions...' }
        }));
        
        await processingService.pollJobStatus(
          response.jobId,
          (status) => {
            setProcessingModal(prev => ({
              ...prev,
              progress: {
                total: images.length,
                completed: status.completed || 0,
                message: status.message || 'Processing...'
              }
            }));
          }
        );
      }
      
      setProcessingModal(prev => ({
        ...prev,
        status: 'completed'
      }));
      
      toast.success(`Successfully captioned ${images.length} images`);
      setSelectedForCaption(new Set());
      setCaptionMode(false);
      
      // Refresh images to show new captions
      if (selectedInfluencer && fetchImagesByFolder) {
        await fetchImagesByFolder('cropped', selectedInfluencer.id);
      }
      
    } catch (error) {
      console.error('Captioning failed:', error);
      setProcessingModal(prev => ({
        ...prev,
        status: 'failed',
        error: error.message || 'Failed to generate captions'
      }));
      toast.error('Failed to generate captions');
    }
  };

  // New handler for opening metadata dialog
  const handleImageClick = async (imageId) => {
    const image = filteredImages.find(img => img.id === imageId);
    if (image) {
      setSelectedImage(image);
      setMetrics(image.metrics || '');
      setScore(image.score !== null && image.score !== undefined ? image.score.toString() : '-1');
      setTextFileContent('');
      setMetadataError(null);
      setMetadataDialogOpen(true);
      
      // Load text file content if available
      await loadTextFileContent(image);
    }
  };

  // Load text file content from S3
  const loadTextFileContent = async (image) => {
    if (!image.s3Key) return;
    
    setLoadingTextFile(true);
    try {
      // Construct the .txt file S3 key
      const txtKey = image.s3Key.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '.txt');
      
      // Fetch the text file content
      const response = await api.get(`/images/text-file/${encodeURIComponent(txtKey)}`);
      if (response.data && response.data.content) {
        setTextFileContent(response.data.content);
      } else {
        setTextFileContent('No text file found for this image');
      }
    } catch (error) {
      console.error('Error loading text file:', error);
      setTextFileContent('Failed to load text file');
    } finally {
      setLoadingTextFile(false);
    }
  };

  // Save text file content only (metrics and score are readonly)
  const handleSaveTextFile = async () => {
    if (!selectedImage) return;
    
    setSavingMetadata(true);
    setMetadataError(null);
    
    try {
      // Save only text file content if changed
      if (textFileContent && selectedImage.s3Key) {
        const txtKey = selectedImage.s3Key.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '.txt');
        await api.put(`/images/text-file/${encodeURIComponent(txtKey)}`, {
          content: textFileContent
        });
      }
      
      setZipSnackbarMessage('Text file saved successfully');
      setZipSnackbarSeverity('success');
      setZipSnackbarOpen(true);
      
      // Close the dialog
      handleCloseMetadataDialog();
    } catch (error) {
      console.error('Error saving text file:', error);
      setMetadataError('Failed to save text file');
    } finally {
      setSavingMetadata(false);
    }
  };

  // Close metadata dialog
  const handleCloseMetadataDialog = () => {
    setMetadataDialogOpen(false);
    setSelectedImage(null);
    setMetrics('');
    setScore('');
    setTextFileContent('');
  };

  // Get current images for the page
  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Cropped & Captioned Images
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          View and manage cropped images with captions. Click on any image to view/edit metrics, score, and associated text files.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {!selectedInfluencer && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select an influencer from the sidebar to view their images.
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">
            {filteredImages.length} images found
          </Typography>
          
          {selectedInfluencer && (
            <>
              {/* Caption controls */}
              {getImagesWithoutCaptions().length > 0 && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleCaptionAll}
                  startIcon={<TextFieldsIcon />}
                  color="success"
                >
                  Caption {getImagesWithoutCaptions().length} Missing
                </Button>
              )}
              
              <Button
                variant={captionMode ? "contained" : "outlined"}
                size="small"
                onClick={() => {
                  setCaptionMode(!captionMode);
                  setSelectedForCaption(new Set());
                }}
                startIcon={<TextFieldsIcon />}
              >
                {captionMode ? 'Cancel' : 'Select to Caption'}
              </Button>
              
              {captionMode && (
                <>
                  <Button
                    size="small"
                    onClick={() => {
                      const allIds = getCurrentImages().map(img => img.id);
                      setSelectedForCaption(new Set(allIds));
                    }}
                  >
                    Select Page
                  </Button>
                  
                  {selectedForCaption.size > 0 && (
                    <>
                      <Chip
                        label={`${selectedForCaption.size} selected`}
                        color="primary"
                        size="small"
                        onDelete={() => setSelectedForCaption(new Set())}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={handleCaptionSelected}
                      >
                        Generate Captions
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel id="images-per-page-label">Images per page</InputLabel>
          <Select
            labelId="images-per-page-label"
            id="images-per-page"
            value={imagesPerPage}
            label="Images per page"
            onChange={handleImagesPerPageChange}
          >
            <MenuItem value={48}>48</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value={500}>500</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {loading && filteredImages.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : filteredImages.length === 0 ? (
        <Alert severity="info">
          No cropped and captioned images found. Process your manually selected images first.
        </Alert>
      ) : (
        <>
          <ImageGrid
            images={getCurrentImages().map(image => {
              const imageUrl = image.url || image.presignedUrl || image.presigned_url;

              return {
                id: image.id,
                url: imageUrl || '',
                caption: image.caption || '',
                metadata: image.metadata || {},
                selected: selectedForCaption.has(image.id)
              };
            })}
            loading={loading}
            folder="cropped"
            onImageClick={captionMode ? (imageId) => {
              const newSelected = new Set(selectedForCaption);
              if (newSelected.has(imageId)) {
                newSelected.delete(imageId);
              } else {
                newSelected.add(imageId);
              }
              setSelectedForCaption(newSelected);
            } : handleImageClick}
            showCaptions={true}
            secondaryActionIcon={<DeleteIcon />}
            secondaryActionTooltip="Delete Image"
            secondaryActionLabel="Delete"
            onSecondaryActionClick={(imageId) => {
              handleDeleteImage(imageId);
            }}
          />
          
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                disabled={loading}
              />
            </Box>
          )}
        </>
      )}
      
      {/* ZIP Creation Snackbar */}
      <Snackbar
        open={zipSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={zipSnackbarMessage}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={zipSnackbarSeverity} 
          sx={{ width: '100%' }}
        >
          {zipSnackbarMessage}
        </Alert>
      </Snackbar>
      
      {/* Caption Edit Dialog */}
      <Dialog 
        open={captionDialogOpen} 
        onClose={handleCloseCaptionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Caption</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <img
                  src={selectedImage.url || selectedImage.presignedUrl || selectedImage.presigned_url || ''}
                  alt={selectedImage.filename}
                  style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                />
              </Box>
              
              {captionError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {captionError}
                </Alert>
              )}
              
              <TextField
                label="Image Caption"
                multiline
                rows={4}
                value={caption}
                onChange={handleCaptionChange}
                fullWidth
                variant="outlined"
                placeholder="Enter a detailed caption for this image..."
                disabled={savingCaption}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCaptionDialog} disabled={savingCaption}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCaption} 
            variant="contained" 
            color="primary"
            disabled={savingCaption}
          >
            {savingCaption ? <CircularProgress size={24} /> : 'Save Caption'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Image Metadata Dialog with Layout */}
      <Dialog
        open={metadataDialogOpen}
        onClose={handleCloseMetadataDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Image Details</DialogTitle>
        <DialogContent>
          {selectedImage && (
            <Box sx={{ width: '100%' }}>
              {metadataError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {metadataError}
                </Alert>
              )}
              
              {/* Main content area with image and side panels */}
              <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
                {/* Score on the left - Read Only */}
                <Box sx={{ width: '200px', flexShrink: 0 }}>
                  <TextField
                    label="Score (Read Only)"
                    value={score}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Score from database"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.secondary',
                        fontWeight: 'bold',
                        fontSize: '1.1rem'
                      }
                    }}
                  />
                </Box>

                {/* Image Display in center */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <img
                    src={selectedImage.url || selectedImage.presignedUrl || selectedImage.presigned_url || ''}
                    alt={selectedImage.filename}
                    style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
                
                {/* Metrics on the right - Read Only */}
                <Box sx={{ width: '200px', flexShrink: 0 }}>
                  <TextField
                    label="Metrics (Read Only)"
                    multiline
                    rows={8}
                    value={metrics}
                    fullWidth
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                    }}
                    helperText="Metrics from database"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.secondary',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </Box>
              </Box>
              
              {/* Text File Content Below */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Associated Text File
                </Typography>
                {loadingTextFile ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <TextField
                    label="Text File Content"
                    multiline
                    rows={8}
                    value={textFileContent}
                    onChange={(e) => setTextFileContent(e.target.value)}
                    fullWidth
                    variant="outlined"
                    placeholder="No text file associated with this image"
                    disabled={savingMetadata}
                    helperText="Edit the associated .txt file content"
                  />
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMetadataDialog} disabled={savingMetadata}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTextFile}
            variant="contained"
            color="primary"
            disabled={savingMetadata || loadingTextFile}
          >
            {savingMetadata ? <CircularProgress size={24} /> : 'Save Text File'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Processing Modal */}
      <ProcessingModal
        open={processingModal.open}
        onClose={() => setProcessingModal(prev => ({ ...prev, open: false }))}
        title={processingModal.title}
        status={processingModal.status}
        progress={processingModal.progress}
        error={processingModal.error}
        canClose={processingModal.status === 'completed' || processingModal.status === 'failed'}
      />
    </Container>
  );
};

export default CroppedCaptionedPage;
